/**
 * UI copy for the KSP Training tile, in the three languages spoken at KSP.
 *
 * TRANSLATION STATUS: the English strings are authoritative. The Spanish and Hmong
 * strings still need a native-speaker review pass — they were written to match the
 * vocabulary already used in the company's own translated training decks (for
 * example "Muaj tseeb"/"Cuav" for True/False, taken from the Hmong BCP deck), but
 * nobody who speaks these languages has signed off on them yet. Fixing a string here
 * is a one-line edit and needs no migration.
 *
 * Any key missing from a language falls back to English rather than rendering blank,
 * so a partial translation is always safe to ship.
 *
 * Placeholders use {name} and are substituted by t():  t('slideCount', { count: 9 })
 */

export const LANGUAGES = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español' },
  { code: 'hm', label: 'Hmong', nativeLabel: 'Hmoob' },
];

export const LANGUAGE_CODES = LANGUAGES.map((l) => l.code);
export const DEFAULT_LANGUAGE = 'en';

/** Short label for a language code, in that language ("Español", not "Spanish"). */
export const nativeLabelFor = (code) =>
  LANGUAGES.find((l) => l.code === code)?.nativeLabel || String(code || '').toUpperCase();

/** English label for a language code, for admin screens and reports. */
export const labelFor = (code) =>
  LANGUAGES.find((l) => l.code === code)?.label || String(code || '').toUpperCase();

const en = {
  // chrome
  tileTitle: 'KSP | Training',
  navDashboard: 'Dashboard',
  navSurveys: 'Surveys',
  navHistory: 'My History',
  navGeneral: 'General',
  backToHQ: 'Back to HQ',
  backToClockIn: 'Back to Clock In',
  extend: 'Extend',
  loading: 'Loading…',
  language: 'Language',
  changeLanguage: 'Change language',

  // language gate
  gateTitle: 'Choose your language',
  gateSubtitle: 'Your trainings will be shown in the language you pick. You can change it any time.',
  gateContinue: 'Continue',

  // dashboard
  dashboardTitle: 'Available Trainings',
  dashboardSubtitle: 'Pick a training to begin, or review one you have finished.',
  dashboardEmpty: 'No trainings available yet. Check back later.',
  slideCount: '{count} slides',
  passThreshold: '{pct}% to pass',
  timeLimit: '{minutes} min',
  availableIn: 'Available in',
  fallbackNotice: 'Not available in {language} yet — shown in English.',
  statusPassed: 'Passed',
  statusInProgress: 'In progress',
  statusNotStarted: 'Not started',
  actionStart: 'Start',
  actionReview: 'Review',
  actionRetry: 'Retry',
  actionContinue: 'Continue',

  // viewer
  back: 'Back',
  passLabel: 'Pass {pct}%',
  slideOf: 'Slide {current} of {total}',
  navPrevious: 'Previous',
  navNext: 'Next',
  loadingSlide: 'Loading slide…',
  noSlidesUploaded: 'No slides uploaded for this training.',
  readyTitle: 'Ready to start the quiz?',
  readyBody: "You've reviewed all the slides. Tap below when you're ready — you need {pct}% to pass.",
  startQuiz: 'Start Quiz',
  starting: 'Starting…',
  noSlides: 'This training has no slides yet.',
  skipToQuiz: 'Skip to Quiz',
  deckFallbackNotice: 'The slides for this training are only available in English.',

  // quiz
  quizFor: 'Quiz: {title}',
  passingScoreLine: 'Passing score: {pct}% · {count} questions',
  unansweredConfirm: '{count} question(s) unanswered. Submit anyway?',
  cancel: 'Cancel',
  submitQuiz: 'Submit Quiz',
  submitting: 'Submitting…',

  // results
  resultPassed: 'You passed!',
  resultFailed: 'Not quite — try again',
  scoreLabel: 'Score:',
  correctOfTotal: '({correct} / {total} correct)',
  reviewHeading: 'Review',
  yourAnswer: 'Your answer:',
  noAnswer: '(no answer)',
  correctAnswer: 'Correct answer:',
  surveyPrompt: 'Got a minute? Share your feedback on this training.',
  takeSurvey: 'Take the survey',
  backToDashboard: 'Back to dashboard',

  // history
  historyTitle: 'My History',
  historySubtitle: 'Your attempts and best scores across all trainings.',
  historyEmpty: "You haven't started any trainings yet.",
  colTraining: 'Training',
  colStatus: 'Status',
  colAttempts: 'Attempts',
  colBestScore: 'Best Score',
  colLastAttempt: 'Last Attempt',
  colLanguage: 'Taken in',

  // surveys
  surveysEmpty: 'No surveys are available right now.',
  surveyQuestionCount: '{count} questions',
  surveyAnonymous: 'Anonymous',
  surveyDone: 'Done',
  surveyRespond: 'Respond',
  surveyRespondAgain: 'Respond again',
  surveyResponded: "You've responded",
  surveyBack: 'Back to Surveys',
  surveyStart: 'Start survey',
  surveyAlreadyResponded: "You've already responded to this survey. Thank you!",
  surveyLanguagePrompt: 'Which language would you like to take this survey in?',
  surveyAnswerRequired: 'Please answer all required questions.',
  surveyQuestionRequired: 'This question is required.',
  surveySubmit: 'Submit response',
  surveyThankYou: 'Thank you!',
  surveyRecorded: 'Your response has been recorded. We appreciate your feedback.',
  surveySubmittedAnonymously: 'Your response was submitted anonymously.',
  surveyBackToList: 'Back to surveys',
  surveySelectAtLeast: 'Select at least {count}.',
  surveySelectUpTo: 'Select up to {count}.',
  surveyNpsLow: 'Not at all likely',
  surveyNpsHigh: 'Extremely likely',
  surveyStartFailed: 'Could not start the survey',
  surveySubmitFailed: 'Failed to submit survey',
};

const es = {
  tileTitle: 'KSP | Capacitación',
  navDashboard: 'Inicio',
  navSurveys: 'Encuestas',
  navHistory: 'Mi Historial',
  navGeneral: 'General',
  backToHQ: 'Volver a HQ',
  backToClockIn: 'Volver a Marcar Entrada',
  extend: 'Extender',
  loading: 'Cargando…',
  language: 'Idioma',
  changeLanguage: 'Cambiar idioma',

  gateTitle: 'Elija su idioma',
  gateSubtitle: 'Sus capacitaciones se mostrarán en el idioma que elija. Puede cambiarlo en cualquier momento.',
  gateContinue: 'Continuar',

  dashboardTitle: 'Capacitaciones Disponibles',
  dashboardSubtitle: 'Elija una capacitación para comenzar o repase una que ya terminó.',
  dashboardEmpty: 'Todavía no hay capacitaciones disponibles. Vuelva más tarde.',
  slideCount: '{count} diapositivas',
  passThreshold: '{pct}% para aprobar',
  timeLimit: '{minutes} min',
  availableIn: 'Disponible en',
  fallbackNotice: 'Aún no está disponible en {language} — se muestra en inglés.',
  statusPassed: 'Aprobado',
  statusInProgress: 'En progreso',
  statusNotStarted: 'Sin comenzar',
  actionStart: 'Comenzar',
  actionReview: 'Repasar',
  actionRetry: 'Reintentar',
  actionContinue: 'Continuar',

  back: 'Atrás',
  passLabel: 'Aprobar {pct}%',
  slideOf: 'Diapositiva {current} de {total}',
  navPrevious: 'Anterior',
  navNext: 'Siguiente',
  loadingSlide: 'Cargando diapositiva…',
  noSlidesUploaded: 'No hay diapositivas cargadas para esta capacitación.',
  readyTitle: '¿Listo para comenzar el examen?',
  readyBody: 'Ya repasó todas las diapositivas. Presione abajo cuando esté listo — necesita {pct}% para aprobar.',
  startQuiz: 'Comenzar Examen',
  starting: 'Comenzando…',
  noSlides: 'Esta capacitación todavía no tiene diapositivas.',
  skipToQuiz: 'Ir al Examen',
  deckFallbackNotice: 'Las diapositivas de esta capacitación solo están disponibles en inglés.',

  quizFor: 'Examen: {title}',
  passingScoreLine: 'Puntaje para aprobar: {pct}% · {count} preguntas',
  unansweredConfirm: '{count} pregunta(s) sin responder. ¿Enviar de todos modos?',
  cancel: 'Cancelar',
  submitQuiz: 'Enviar Examen',
  submitting: 'Enviando…',

  resultPassed: '¡Aprobó!',
  resultFailed: 'Casi — inténtelo de nuevo',
  scoreLabel: 'Puntaje:',
  correctOfTotal: '({correct} / {total} correctas)',
  reviewHeading: 'Repaso',
  yourAnswer: 'Su respuesta:',
  noAnswer: '(sin respuesta)',
  correctAnswer: 'Respuesta correcta:',
  surveyPrompt: '¿Tiene un minuto? Comparta su opinión sobre esta capacitación.',
  takeSurvey: 'Responder la encuesta',
  backToDashboard: 'Volver al inicio',

  historyTitle: 'Mi Historial',
  historySubtitle: 'Sus intentos y mejores puntajes en todas las capacitaciones.',
  historyEmpty: 'Todavía no ha comenzado ninguna capacitación.',
  colTraining: 'Capacitación',
  colStatus: 'Estado',
  colAttempts: 'Intentos',
  colBestScore: 'Mejor Puntaje',
  colLastAttempt: 'Último Intento',
  colLanguage: 'Tomada en',

  surveysEmpty: 'No hay encuestas disponibles en este momento.',
  surveyQuestionCount: '{count} preguntas',
  surveyAnonymous: 'Anónima',
  surveyDone: 'Listo',
  surveyRespond: 'Responder',
  surveyRespondAgain: 'Responder de nuevo',
  surveyResponded: 'Ya respondió',
  surveyBack: 'Volver a Encuestas',
  surveyStart: 'Comenzar encuesta',
  surveyAlreadyResponded: 'Ya respondió esta encuesta. ¡Gracias!',
  surveyLanguagePrompt: '¿En qué idioma desea responder esta encuesta?',
  surveyAnswerRequired: 'Por favor responda todas las preguntas obligatorias.',
  surveyQuestionRequired: 'Esta pregunta es obligatoria.',
  surveySubmit: 'Enviar respuestas',
  surveyThankYou: '¡Gracias!',
  surveyRecorded: 'Su respuesta fue registrada. Agradecemos sus comentarios.',
  surveySubmittedAnonymously: 'Su respuesta se envió de forma anónima.',
  surveyBackToList: 'Volver a encuestas',
  surveySelectAtLeast: 'Seleccione al menos {count}.',
  surveySelectUpTo: 'Seleccione hasta {count}.',
  surveyNpsLow: 'Nada probable',
  surveyNpsHigh: 'Muy probable',
  surveyStartFailed: 'No se pudo comenzar la encuesta',
  surveySubmitFailed: 'No se pudo enviar la encuesta',
};

const hm = {
  tileTitle: 'KSP | Kev Cob Qhia',
  navDashboard: 'Nplooj Tseem Ceeb',
  navSurveys: 'Cov Lus Nug Ntsuas',
  navHistory: 'Kuv Cov Ntaub Ntawv',
  navGeneral: 'Dav Dav',
  backToHQ: 'Rov Qab Mus HQ',
  backToClockIn: 'Rov Qab Mus Scan Sij Hawm',
  extend: 'Ntxiv Sij Hawm',
  loading: 'Tos me ntsis…',
  language: 'Yam Lus',
  changeLanguage: 'Hloov yam lus',

  gateTitle: 'Xaiv koj yam lus',
  gateSubtitle: 'Koj cov kev cob qhia yuav siv yam lus koj xaiv. Koj hloov tau txhua lub sij hawm.',
  gateContinue: 'Mus Ntxiv',

  dashboardTitle: 'Cov Kev Cob Qhia',
  dashboardSubtitle: 'Xaiv ib qho kev cob qhia los pib, los sis rov saib ib qho koj ua tiav lawm.',
  dashboardEmpty: 'Tsis muaj kev cob qhia tam sim no. Rov qab los saib dua.',
  slideCount: '{count} nplooj',
  passThreshold: '{pct}% thiaj dhau',
  timeLimit: '{minutes} feeb',
  availableIn: 'Muaj ua',
  fallbackNotice: 'Tsis muaj ua {language} tam sim no — siv lus Askiv.',
  statusPassed: 'Dhau lawm',
  statusInProgress: 'Tseem ua',
  statusNotStarted: 'Tsis tau pib',
  actionStart: 'Pib',
  actionReview: 'Rov Saib',
  actionRetry: 'Rov Ua Dua',
  actionContinue: 'Mus Ntxiv',

  back: 'Rov Qab',
  passLabel: 'Dhau {pct}%',
  slideOf: 'Nplooj {current} ntawm {total}',
  navPrevious: 'Rov Qab',
  navNext: 'Tom Ntej',
  loadingSlide: 'Tos nplooj…',
  noSlidesUploaded: 'Tsis muaj nplooj rau kev cob qhia no.',
  readyTitle: 'Npaj txhij ua cov lus nug lawm?',
  readyBody: 'Koj saib tag nrho cov nplooj lawm. Nias hauv qab thaum koj npaj txhij — koj xav tau {pct}% thiaj dhau.',
  startQuiz: 'Pib Cov Lus Nug',
  starting: 'Pib…',
  noSlides: 'Kev cob qhia no tsis muaj nplooj.',
  skipToQuiz: 'Mus Rau Cov Lus Nug',
  deckFallbackNotice: 'Cov nplooj rau kev cob qhia no muaj ua lus Askiv xwb.',

  quizFor: 'Cov Lus Nug: {title}',
  passingScoreLine: 'Qhab nia thiaj dhau: {pct}% · {count} lus nug',
  unansweredConfirm: '{count} lus nug tsis tau teb. Xa mus li ntawd?',
  cancel: 'Tso Tseg',
  submitQuiz: 'Xa Cov Lus Teb',
  submitting: 'Xa…',

  resultPassed: 'Koj dhau lawm!',
  resultFailed: 'Tsis tau dhau — rov ua dua',
  scoreLabel: 'Qhab nia:',
  correctOfTotal: '({correct} / {total} raug)',
  reviewHeading: 'Rov Saib',
  yourAnswer: 'Koj cov lus teb:',
  noAnswer: '(tsis tau teb)',
  correctAnswer: 'Cov lus teb raug:',
  surveyPrompt: 'Muaj ib feeb? Qhia koj cov tswv yim txog kev cob qhia no.',
  takeSurvey: 'Teb cov lus nug ntsuas',
  backToDashboard: 'Rov qab mus nplooj tseem ceeb',

  historyTitle: 'Kuv Cov Ntaub Ntawv',
  historySubtitle: 'Koj cov kev sim thiab qhab nia zoo tshaj hauv txhua kev cob qhia.',
  historyEmpty: 'Koj tsis tau pib ib qho kev cob qhia.',
  colTraining: 'Kev Cob Qhia',
  colStatus: 'Xwm Txheej',
  colAttempts: 'Sim Pes Tsawg Zaus',
  colBestScore: 'Qhab Nia Zoo Tshaj',
  colLastAttempt: 'Zaum Kawg',
  colLanguage: 'Ua Yam Lus',

  surveysEmpty: 'Tsis muaj cov lus nug ntsuas tam sim no.',
  surveyQuestionCount: '{count} lus nug',
  surveyAnonymous: 'Tsis qhia npe',
  surveyDone: 'Tiav lawm',
  surveyRespond: 'Teb',
  surveyRespondAgain: 'Teb dua',
  surveyResponded: 'Koj teb lawm',
  surveyBack: 'Rov Qab Mus Cov Lus Nug Ntsuas',
  surveyStart: 'Pib teb',
  surveyAlreadyResponded: 'Koj twb teb cov lus nug ntsuas no lawm. Ua tsaug!',
  surveyLanguagePrompt: 'Koj xav teb cov lus nug ntsuas no ua yam lus twg?',
  surveyAnswerRequired: 'Thov teb txhua lo lus nug uas yuav tsum teb.',
  surveyQuestionRequired: 'Lo lus nug no yuav tsum teb.',
  surveySubmit: 'Xa cov lus teb',
  surveyThankYou: 'Ua tsaug!',
  surveyRecorded: 'Peb tau txais koj cov lus teb lawm. Ua tsaug rau koj cov tswv yim.',
  surveySubmittedAnonymously: 'Koj cov lus teb raug xa tsis qhia npe.',
  surveyBackToList: 'Rov qab mus cov lus nug ntsuas',
  surveySelectAtLeast: 'Xaiv tsawg kawg {count}.',
  surveySelectUpTo: 'Xaiv tau txog {count}.',
  surveyNpsLow: 'Tsis muaj kiag li',
  surveyNpsHigh: 'Muaj heev',
  surveyStartFailed: 'Pib tsis tau',
  surveySubmitFailed: 'Xa tsis tau',
};

export const STRINGS = { en, es, hm };

/**
 * Look up a string for a language, substituting {placeholders}.
 * Falls back to English, then to the key itself, so a missing translation degrades
 * to readable text instead of a blank.
 */
export const translate = (language, key, vars) => {
  const table = STRINGS[language] || STRINGS[DEFAULT_LANGUAGE];
  let text = table[key];
  if (text === undefined) text = STRINGS[DEFAULT_LANGUAGE][key];
  if (text === undefined) return key;
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (match, name) =>
    (vars[name] === undefined || vars[name] === null ? match : String(vars[name]))
  );
};

export default STRINGS;
